import { Field, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { IOrganisation } from "../../../../customTypings/company/Organisation";
import { IRole } from "../../../../customTypings/user/Role";
import { organisationService } from "../../../../services";
import { rolesService } from "../../../../services";
import { userService } from "../../../../services";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    shouldShowFormFieldError,
    showToastError,
} from "../../../../utils";

type AddNewUserProps = {
    show: boolean;
    onClose: () => void;
    onAdded?: () => void;
};

interface IActionResponse {
    status: string;
    message: string;
    entity?: any;
}

type InviteFormValues = {
    email: string;
    roles: string[];
    organisation: string;
};

/** Align with IdentityInviteViewModel (email, organisation required; roles expected). */
const inviteUserValidationSchema = Yup.object({
    email: Yup.string()
        .trim()
        .email("Enter a valid email address.")
        .max(256, "Email must be at most 256 characters.")
        .required("Email is required."),
    roles: Yup.array()
        .of(Yup.string().required())
        .min(1, "Select at least one role.")
        .required(),
    organisation: Yup.string().required("Select an organisation."),
});

const AddNewUser: React.FC<AddNewUserProps> = ({ show, onClose, onAdded }) => {
    const empty: string[] = [];
    const [alertVariant, setAlertVariant] = useState("danger");
    const [organisations, setOrganisations] = useState<IOrganisation[]>();
    const [allRoles, setAllRoles] = useState<IRole[]>();
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const validationSchema = useMemo(() => inviteUserValidationSchema, []);

    useEffect(() => {
        organisationService.getAll().then((orgs: IOrganisation[]) => setOrganisations(orgs));
        rolesService.getAllForGroup(0).then((roles: IRole[]) => setAllRoles(roles));
    }, []);

    return (
        <Modal centered show={show} keyboard={false} onHide={onClose}>
            <Formik<InviteFormValues>
                initialValues={{
                    email: "",
                    roles: empty,
                    organisation: "",
                }}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values, { setStatus, setSubmitting }) => {
                    setLiveValidationAfterSubmit(true);
                    setStatus(undefined);
                    setSubmitting(true);
                    try {
                        const response = (await userService.add(
                            values.email,
                            values.roles,
                            values.organisation
                        )) as IActionResponse;
                        if (response.status === "Success") {
                            setAlertVariant("success");
                            setStatus("Invitation sent successfully.");
                            setLiveValidationAfterSubmit(false);
                            setTimeout(() => {
                                if (typeof onAdded === "function") onAdded();
                                onClose();
                            }, 3000);
                        } else {
                            setAlertVariant("danger");
                            setStatus(response.message);
                            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                        }
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, status, isSubmitting, handleSubmit, submitCount }) => {
                    const hasPersistedRecord = false;
                    const showErr = (field: keyof InviteFormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit
                        );
                    return (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton>
                                <Modal.Title>Invite New User</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group as={Row} className="mb-3" controlId="newUserEmail">
                                    <Form.Label column sm={3}>
                                        Email <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Col sm={9}>
                                        <Field
                                            name="email"
                                            type="email"
                                            autoFocus
                                            className={`form-control${showErr("email") ? " is-invalid" : ""}`}
                                            aria-invalid={showErr("email")}
                                        />
                                        {showErr("email") && (
                                            <div className="invalid-feedback d-block">{errors.email}</div>
                                        )}
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="roleCheckboxs">
                                    <Form.Label column sm={3}>
                                        Roles <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Col sm={9} className="pt-2">
                                        {allRoles &&
                                            allRoles.map((role) => (
                                                <label key={role.id} className="d-block">
                                                    <Field
                                                        type="checkbox"
                                                        name="roles"
                                                        value={role.id}
                                                        className="form-check-input me-2"
                                                    />
                                                    {role.name}
                                                </label>
                                            ))}
                                        {showErr("roles") && (
                                            <div className="invalid-feedback d-block">{errors.roles as string}</div>
                                        )}
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="organisationRadios">
                                    <Form.Label column sm={3}>
                                        Organisation <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Col sm={9} className="pt-2">
                                        {organisations &&
                                            organisations
                                                .filter((organisation) => organisation.displayName === "Froom")
                                                .map((organisation) => (
                                                    <label key={organisation.id}>
                                                        <Field
                                                            type="radio"
                                                            name="organisation"
                                                            value={organisation.id}
                                                            className="mx-2"
                                                        />
                                                        {organisation.displayName}
                                                    </label>
                                                ))}
                                        {showErr("organisation") && (
                                            <div className="invalid-feedback d-block">{errors.organisation}</div>
                                        )}
                                    </Col>
                                </Form.Group>

                                {status && (
                                    <Alert variant={alertVariant} className="mt-3">
                                        {status}
                                    </Alert>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <div className="form-group">
                                    <Button variant="primary" disabled={isSubmitting} type="submit" className="me-2">
                                        {isSubmitting ? (
                                            <LoadingSpinner text="Sending invitation..." />
                                        ) : (
                                            "Invite User"
                                        )}
                                    </Button>
                                    <Button variant="secondary" onClick={onClose}>
                                        Cancel
                                    </Button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export { AddNewUser };
